import { Component, OnDestroy } from '@angular/core';
import { UntypedFormArray, UntypedFormGroup } from '@angular/forms';
import { ItemReorderEventDetail } from '@ionic/angular';
import { debounceTime, Subject, takeUntil } from 'rxjs';

import { createForm, patchForm } from '../../config/base-form.config';
import { BaseFormControl } from '../base-control';
import { FormBlocksField, FormField } from '../form-field';

import { blockFieldsFor, DEFAULT_DISCRIMINATOR, DEFAULT_KEY_FIELD, nextBlockKey } from './block-fields';

@Component({
    standalone: false,
    selector: 'abs-block-editor',
    templateUrl: './block-editor.component.html',
    styles: [
        `
            .block-editor {
                display: grid;
                grid-template-columns: 240px 1fr 1fr;
                gap: 12px;
                align-items: start;
            }
            .block-row {
                cursor: pointer;
            }
            .block-row.selected {
                --background: var(--ion-color-primary-tint);
            }
            /* A hidden block that looks like a live one is a trap: hiding is how content gets
               parked, so it has to be obvious at a glance which blocks are parked. */
            .block-row.is-hidden {
                opacity: 0.45;
                font-style: italic;
            }
            .preview {
                border: 1px solid var(--ion-color-light-shade);
                padding: 8px;
                overflow: auto;
                max-height: 70vh;
            }
        `
    ]
})
export class BlockEditorComponent extends BaseFormControl implements OnDestroy {
    field: FormBlocksField;

    /** Index of the block being edited, or null when none is open. */
    selectedIndex: number | null = null;

    private readonly destroy$ = new Subject<void>();

    get blocks(): UntypedFormArray {
        return this.parentFormGroup.get(this.field.name) as UntypedFormArray;
    }

    get discriminator(): string {
        return this.field.discriminator ?? DEFAULT_DISCRIMINATOR;
    }

    get keyField(): string {
        return this.field.keyField ?? DEFAULT_KEY_FIELD;
    }

    /**
     * Everything the template binds is precomputed here, never called as a method from the
     * template.
     *
     * This repo forbids plain method calls in Angular templates: they re-run on every
     * change-detection cycle, so `previewHtml(block)` in a binding would re-render the preview
     * constantly. Precomputing is also what makes the debounce possible — there is nowhere to put a
     * debounce if the template drives the work.
     */
    rows: { index: number; label: string; hidden: boolean }[] = [];
    selectedFields: FormField[] = [];
    selectedType = '';
    preview = '';

    /**
     * Zero- or one-element list the editor pane iterates, holding a value that changes every time
     * the pane must be rebuilt from scratch instead of reused. See the comment in the template for
     * why reuse is wrong; the counter is what the template's `trackBy` compares.
     */
    editorKey: number[] = [];
    private nextEditorKey = 0;

    trackByIndex = (_: number, row: { index: number }) => row.index;
    trackBySelf = (_: number, key: number) => key;

    ngOnInit(): void {
        super.ngOnInit();
        this.refreshRows();
        // Row labels follow the content, so they update as you type; the preview is the expensive
        // half and is debounced.
        this.blocks.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
            // Must run before refreshRows/refreshPreview: patchForm clears and rebuilds this whole
            // FormArray on every `value` input change on the same form instance (see
            // base-form.config.ts), and a rebuild shorter than the current selection leaves
            // selectedIndex pointing past the end.
            this.clearStaleSelection();
            this.refreshRows();
        });
        this.blocks.valueChanges.pipe(debounceTime(200), takeUntil(this.destroy$)).subscribe(() => this.refreshPreview());
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Nulls `selectedIndex` once it no longer points at a real block, rather than merely guarding
     * reads of it. `patchForm` clears and rebuilds the whole blocks `FormArray` on every `value`
     * input change on the same form instance, so a rebuild shorter than the current selection
     * leaves `selectedIndex` stale. A guard flag would need every present and future reader —
     * template included — to remember to consult it; removing the invalid state instead makes every
     * reader safe by construction.
     */
    clearStaleSelection(): void {
        if (this.selectedIndex !== null && this.selectedIndex >= this.blocks.length) {
            this.selectedIndex = null;
            this.refreshSelectedFields();
            this.refreshPreview();
        }
    }

    /**
     * Whether `selectedIndex` still points at a real block. `clearStaleSelection` is what keeps
     * `selectedIndex` itself from going stale; this is a defensive second check on top of that, not
     * the primary guard, since `refreshSelectedFields`/`refreshPreview` are also called directly
     * (e.g. from `select`) without going through `clearStaleSelection` first.
     */
    private get hasValidSelection(): boolean {
        return this.selectedIndex !== null && this.selectedIndex < this.blocks.length;
    }

    refreshRows(): void {
        this.rows = this.blocks.value.map((block: any, index: number) => ({
            index,
            label: this.field.describeBlock ? this.field.describeBlock(block) : String(block?.[this.discriminator] ?? ''),
            hidden: !!block?.hidden
        }));
    }

    refreshPreview(): void {
        const block = this.hasValidSelection ? this.blocks.at(this.selectedIndex).value : null;
        this.preview = block && this.field.renderPreview ? this.field.renderPreview(block) : '';
    }

    refreshSelectedFields(): void {
        // Every path that opens, closes, replaces or re-indexes the edited block ends up here, and
        // nothing else does — the value-change subscriptions only refresh the rows and the preview.
        // So this is the one place that has to invalidate the editor pane, and typing does not
        // rebuild it.
        // Gated on `hasValidSelection`, not on `selectedIndex !== null`: an index past the end of
        // the array must not reach `formGroupName`, which would throw looking the group up.
        this.editorKey = this.hasValidSelection ? [this.nextEditorKey++] : [];

        if (!this.hasValidSelection) {
            this.selectedFields = [];
            this.selectedType = '';
            return;
        }
        const block = this.blocks.at(this.selectedIndex).value;
        this.selectedType = block?.[this.discriminator] ?? '';
        // Drops the leading key and discriminator, which are hidden controls the component renders
        // itself as the list label and the type picker.
        this.selectedFields = blockFieldsFor(this.field, block?.[this.discriminator]).slice(2);
    }

    select(index: number): void {
        this.selectedIndex = this.selectedIndex === index ? null : index;
        this.refreshSelectedFields();
        this.refreshPreview();
    }

    addBlock(type: string, content: any = {}): void {
        const key = nextBlockKey(
            this.blocks.value.map((b: any) => b?.[this.keyField]),
            this.field.keyPrefix ?? 'block',
            type
        );
        const block = { ...content, [this.keyField]: key, [this.discriminator]: type };
        this.blocks.push(this.buildGroup(block));
        this.selectedIndex = this.blocks.length - 1;
        this.refreshRows();
        this.refreshSelectedFields();
        this.refreshPreview();
    }

    deleteBlock(index: number): void {
        this.blocks.removeAt(index);
        this.selectedIndex = null;
        this.refreshRows();
        this.refreshSelectedFields();
        this.refreshPreview();
    }

    duplicateBlock(index: number): void {
        const source = { ...this.blocks.at(index).value };
        const type = source[this.discriminator];
        delete source[this.keyField];
        this.addBlock(type, source);
    }

    moveBlock(from: number, to: number): void {
        const group = this.blocks.at(from);
        this.blocks.removeAt(from);
        this.blocks.insert(to, group);
        this.selectedIndex = null;
        this.refreshRows();
        this.refreshSelectedFields();
        this.refreshPreview();
    }

    doReorder(ev: CustomEvent<ItemReorderEventDetail>): void {
        ev.stopPropagation();
        const { from, to, complete } = ev.detail;
        this.moveBlock(from, to);
        complete();
    }

    /**
     * Rebuilds a block's controls for a new type.
     *
     * Angular fixes a FormGroup's controls at construction, so this replaces the group rather than
     * mutating it. Fields belonging to the old type are dropped, not carried: RulebookBlock is a
     * discriminated union precisely so an image block cannot hold a stale body, and `entities` is
     * `strict: false` so nothing at rest would reject one.
     */
    changeBlockType(index: number, type: string): void {
        const previous = this.blocks.at(index).value;
        const block = { [this.keyField]: previous?.[this.keyField], [this.discriminator]: type };
        this.blocks.setControl(index, this.buildGroup(block));
        this.refreshRows();
        this.refreshSelectedFields();
        this.refreshPreview();
    }

    private buildGroup(block: any): UntypedFormGroup {
        const fields = blockFieldsFor(this.field, block[this.discriminator]);
        const group = createForm(fields);
        patchForm(group, fields, block);
        group.patchValue(block);
        return group;
    }
}
